/**
 * Backend selection by configuration. The game picks a backend once at startup; gameplay code only ever
 * sees the GameServices facade, so switching between the own server (WebSocket/HTTP) and the offline
 * in-process backend does not touch gameplay.
 *
 * Accepted specs (from a URL query `?backend=`, `VITE_FWP_BACKEND` or a launcher setting):
 *   '' / 'same-origin'                 -> the server the page came from: WebSocket `ws(s)://<page host>/ws`
 *                                         (the Vite dev proxy and the self-hosted server both provide it).
 *                                         Outside a browser page (Node: tests, headless tools, the server's
 *                                         validator) '' keeps the in-process backend; 'same-origin' fails.
 *   'offline' / 'in-process' / 'local' -> in-process backend, persisted in localStorage (memory in Node)
 *   'memory'                           -> in-process backend without persistence
 *   'http://host:8787' / 'https://...' -> HttpTransport
 *   'ws://host:8787/ws' / 'wss://...'  -> WebSocketTransport
 * An unavailable server is never replaced by the offline backend silently: offline play is always explicit.
 *
 * The page's `?backend=` query (pageBackendSpec) may name a server address only on the Vite dev server: on a
 * production page (the self-hosted server's dist/, the Pages site) a link with `?backend=wss://another.example/ws`
 * would make the official page send the nickname and password typed into its sign-in screen to that server. There
 * the query picks only the in-process backends (offline, memory) or same-origin; another server is chosen when the
 * game is built (VITE_FWP_BACKEND).
 */
import { DEV_HOOKS } from './build-flags';
import type { InProcessBackendOptions } from './net/in-process';
import type { WebSocketConstructor } from './net/websocket';

export type BackendConnectionConfig =
  | ({ transport: 'in-process' } & InProcessBackendOptions)
  | { transport: 'http'; baseUrl: string; fetch?: typeof fetch; pollWaitMs?: number; timeoutMs?: number }
  | { transport: 'websocket'; url: string; WebSocketImpl?: WebSocketConstructor; timeoutMs?: number };

/** The part of `window.location` the same-origin default needs. */
export interface PageLocation {
  protocol: string;
  host: string;
}

export interface BackendSpecOptions {
  /** Page the game runs on (default: `globalThis.location` in browsers; none in Node). `null` = no page. */
  location?: PageLocation | null;
}

function pageLocation(): PageLocation | null {
  const loc = (globalThis as { location?: PageLocation }).location;
  return loc && typeof loc.protocol === 'string' && typeof loc.host === 'string' ? loc : null;
}

/** WebSocket endpoint of the server that served the page. */
export function sameOriginBackend(loc: PageLocation): BackendConnectionConfig {
  if (loc.protocol !== 'http:' && loc.protocol !== 'https:') {
    throw new Error(`The same-origin backend needs a page served over http(s), not "${loc.protocol}" (use ?backend=offline)`);
  }
  return { transport: 'websocket', url: `${loc.protocol === 'https:' ? 'wss' : 'ws'}://${loc.host}/ws` };
}

export function parseBackendSpec(spec: string | null | undefined, opts: BackendSpecOptions = {}): BackendConnectionConfig {
  const s = (spec ?? '').trim();
  if (s === '' || s === 'same-origin') {
    const loc = opts.location !== undefined ? opts.location : pageLocation();
    if (loc) return sameOriginBackend(loc);
    if (s === 'same-origin') throw new Error('The same-origin backend needs a browser page');
    return { transport: 'in-process' };
  }
  if (s === 'offline' || s === 'in-process' || s === 'local') return { transport: 'in-process' };
  if (s === 'memory') return { transport: 'in-process', storage: 'memory' };
  let url: URL;
  try {
    url = new URL(s);
  } catch {
    throw new Error(`Invalid backend spec "${s}"`);
  }
  if (url.protocol === 'http:' || url.protocol === 'https:') return { transport: 'http', baseUrl: `${url.protocol}//${url.host}${url.pathname.replace(/\/(api)?\/?$/, '')}` };
  if (url.protocol === 'ws:' || url.protocol === 'wss:') {
    if (url.pathname === '/' || url.pathname === '') url.pathname = '/ws';
    return { transport: 'websocket', url: url.toString() };
  }
  throw new Error(`Unsupported backend protocol "${url.protocol}"`);
}

/** A spec that names a server address (http(s)://, ws(s)://) rather than a built-in backend. */
export function isAddressSpec(spec: string): boolean {
  return /^[a-z][a-z0-9+.-]*:/i.test(spec.trim());
}

/**
 * The backend spec of a browser page: the `?backend=` query parameter (`param`), else the build's VITE_FWP_BACKEND
 * (`buildSpec`). A server address in the query is used only with `allowAddressParam` (the Vite dev server); otherwise
 * it is ignored (`ignored` names it, for a console warning) and the build's backend is used (see the module comment).
 */
export function pageBackendSpec(param: string | null, buildSpec: string | undefined, opts: { allowAddressParam: boolean }): { spec: string; ignored: string | null } {
  const p = param?.trim() ?? '';
  if (p !== '' && (opts.allowAddressParam || !isAddressSpec(p))) return { spec: p, ignored: null };
  return { spec: buildSpec ?? '', ignored: p === '' ? null : p };
}

/** The query string of the page the game runs on (none outside a browser page). */
function pageSearch(): string | null {
  const loc = (globalThis as { location?: { search?: unknown } }).location;
  return loc && typeof loc.search === 'string' ? loc.search : null;
}

/**
 * The test/dev page switch `autoguest=1` (`?backend=offline&autoguest=1`): the game signs in as a guest instead of
 * showing its sign-in screen, so browser smoke tests of gameplay need no account. Honoured ONLY for the in-process
 * backends (offline, memory): a server's players always sign up or sign in. Never in a build without the developer
 * hooks (the Android release build: build-flags.ts).
 */
export function autoGuestRequested(config: BackendConnectionConfig, search: string | null = pageSearch()): boolean {
  if (!DEV_HOOKS || config.transport !== 'in-process' || !search) return false;
  return new URLSearchParams(search).get('autoguest') === '1';
}

/**
 * The test/dev page switch `battleseed=N` (0..399, with `?backend=offline|memory`): every battle of the in-process
 * backend uses this seed, so a browser test plays a known battle, the same in every run. Honoured ONLY for the
 * in-process backends: a server's seed comes from its own RNG (BATTLE_SEED there). Never in a build without the
 * developer hooks (the Android release build: build-flags.ts).
 */
export function battleSeedRequested(config: BackendConnectionConfig, search: string | null = pageSearch()): number | null {
  if (!DEV_HOOKS || config.transport !== 'in-process' || !search) return null;
  const raw = new URLSearchParams(search).get('battleseed');
  if (raw === null || !/^\d{1,3}$/.test(raw)) return null;
  const n = Number(raw);
  return n <= 399 ? n : null;
}

/** Address shown to the player (loading screen): the server URL, or 'offline' / 'memory'. */
export function describeBackend(config: BackendConnectionConfig): string {
  switch (config.transport) {
    case 'http':
      return config.baseUrl;
    case 'websocket':
      return config.url;
    case 'in-process':
      return config.storage === 'memory' ? 'memory' : 'offline';
  }
}

/**
 * Origin that identifies a network backend for its credentials: `http(s)://host[:port]`. HTTP and WebSocket
 * endpoints of one server share it (ws -> http, wss -> https), so switching the transport keeps the account.
 * Null for the in-process backend.
 */
export function backendOrigin(config: BackendConnectionConfig): string | null {
  if (config.transport === 'in-process') return null;
  const url = new URL(config.transport === 'http' ? config.baseUrl : config.url);
  const scheme = url.protocol === 'wss:' || url.protocol === 'https:' ? 'https:' : 'http:';
  return `${scheme}//${url.host}`;
}

/**
 * Why a server address typed by the player (the sign-in screen's "Server" setting) or given to a build
 * (VITE_BACKEND_URL) is refused: 'empty'; 'invalid' (not an address, or with a query / fragment); 'protocol' (another
 * scheme than http(s) / ws(s)); 'credentials' (user:password@host: a password never goes into an address); 'insecure'
 * (http:// / ws:// or a bare host in a build that connects over TLS only: the Android release APK, build-flags.ts).
 */
export type ServerAddressProblem = 'empty' | 'invalid' | 'protocol' | 'credentials' | 'insecure';

/** Longest server address accepted. */
export const SERVER_ADDRESS_MAX = 200;

/**
 * A server address as a backend spec of the game's own server (its WebSocket endpoint): `host[:port]` (a LAN IP, the
 * Android emulator's host alias 10.0.2.2) means http://; http(s):// becomes ws(s):// (the self-hosted server and the
 * Pages proxy serve `/ws` next to `/api`); the path `/`, `/api` or none means `/ws`, another path is kept (a server
 * behind a reverse proxy). No query, fragment or user info. `secureOnly` (a build without cleartext: the Android
 * release APK) refuses every address that would not use TLS (http://, ws://, a bare host).
 */
export function normalizeServerAddress(
  text: string,
  opts: { secureOnly?: boolean } = {},
): { ok: true; spec: string } | { ok: false; problem: ServerAddressProblem } {
  const s = (text ?? '').trim();
  if (s === '') return { ok: false, problem: 'empty' };
  if (s.length > SERVER_ADDRESS_MAX || /\s/.test(s)) return { ok: false, problem: 'invalid' };
  const scheme = /^([a-z][a-z0-9+.-]*):\/\//i.exec(s);
  if (!scheme && /^[a-z][a-z0-9+.-]*:(?!\d)/i.test(s)) return { ok: false, problem: 'protocol' };
  let url: URL;
  try {
    url = new URL(scheme ? s : `http://${s}`);
  } catch {
    return { ok: false, problem: 'invalid' };
  }
  const protocol = url.protocol.toLowerCase();
  if (!['http:', 'https:', 'ws:', 'wss:'].includes(protocol)) return { ok: false, problem: 'protocol' };
  if (url.username || url.password) return { ok: false, problem: 'credentials' };
  if (!url.hostname || url.search || url.hash || s.includes('?') || s.includes('#')) return { ok: false, problem: 'invalid' };
  const secure = protocol === 'https:' || protocol === 'wss:';
  if (opts.secureOnly && !secure) return { ok: false, problem: 'insecure' };
  let path = url.pathname.replace(/\/+$/, '');
  if (path === '' || path === '/api') path = '/ws';
  return { ok: true, spec: `${secure ? 'wss' : 'ws'}://${url.host}${path}` };
}

/**
 * The backend spec of a start of the game, first match wins: the page's `?backend=` (pageBackendSpec: a server address
 * only on the dev server), the server the player chose (`stored`, the sign-in screen's setting, where the build offers
 * it), the build's server address (`buildUrl`: VITE_BACKEND_URL, e.g. the Android app's default server), the build's
 * backend (VITE_FWP_BACKEND), else the page's own origin. `source` says which one it was. `secureOnly` (a build
 * without cleartext) skips a stored or build address that would not use TLS.
 */
export function startupBackendSpec(input: {
  param: string | null;
  allowAddressParam: boolean;
  stored?: string | null;
  buildUrl?: string | null;
  buildSpec?: string | null;
  secureOnly?: boolean;
}): { spec: string; source: 'param' | 'stored' | 'build-url' | 'build' | 'default'; ignored: string | null } {
  const page = pageBackendSpec(input.param, undefined, { allowAddressParam: input.allowAddressParam });
  if (page.spec !== '') return { spec: page.spec, source: 'param', ignored: null };
  const opts = { secureOnly: input.secureOnly === true };
  const stored = input.stored ? normalizeServerAddress(input.stored, opts) : null;
  if (stored?.ok) return { spec: stored.spec, source: 'stored', ignored: page.ignored };
  const url = input.buildUrl ? normalizeServerAddress(input.buildUrl, opts) : null;
  if (url?.ok) return { spec: url.spec, source: 'build-url', ignored: page.ignored };
  const build = (input.buildSpec ?? '').trim();
  if (build !== '') return { spec: build, source: 'build', ignored: page.ignored };
  return { spec: '', source: 'default', ignored: page.ignored };
}
